/**
 * src/pages/FallacyLibrary/sections/IntroSection.tsx
 * Comprehensive educational introduction to informal and formal fallacies.
 *
 * Mandated content:
 * - Difference between Formal and Informal fallacies
 * - Rules of epistemic engagement:
 *   1. Naming a fallacy never refutes a conclusion (The Fallacy Fallacy / argumentum ad logicam)
 *   2. Do not use fallacy names as rhetorical insults or to label real people dishonest
 *   3. When emotional information, expert testimony, and historical precedents ARE reasonable
 *   4. Understanding valid deductive and inductive boundaries
 */

import React from "react";
import { Deeper } from "@/components/knowledge/Deeper";
import { LessonStep } from "@/components/knowledge/LessonStep";
import { ScopeNote } from "@/components/knowledge/ScopeNote";
import { Term } from "@/components/knowledge/Term";
import { AlertTriangle, CheckCircle2, HelpCircle, Lightbulb, Scale, ShieldAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import "@/components/cosmic/cosmic.css";
export function IntroSection() {
  return <div className="space-y-6 mb-12">
      {/* Foundational Framing */}
      <div className="ql-glass p-5 sm:p-8 rounded-xl border border-border/80 bg-card/60">
        <div className="flex items-center gap-2.5 mb-3">
          <Scale className="size-5 text-primary shrink-0" aria-hidden="true" />
          <h2 className="font-serif text-lg sm:text-2xl font-bold text-foreground">
            The Taxonomy of Flawed Inferences
          </h2>
        </div>

        <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
          A <strong className="text-foreground">fallacy</strong> is a defect in reasoning that weakens or destroys the logical justification of an argument. Reasoning breakdowns are not arbitrary mistakes: they follow identifiable cognitive and structural patterns that recur across politics, science, law, media, and everyday human conversation.
        </p>

        {/* Dual Classification: Formal vs Informal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="ql-card p-4 sm:p-5 rounded-lg border border-border/60 bg-card/50">
            <span className="fl-telemetry-tag block w-fit mb-2">Deductive Defects</span>
            <h3 className="font-serif text-base font-bold text-foreground mb-2 flex items-center gap-2">
              <AlertTriangle className="size-4 text-emerald-400 shrink-0" aria-hidden="true" />
              <span>Formal Fallacies</span>
            </h3>
            <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Errors in the <strong className="text-foreground">pure structural syntax</strong> of an argument. In a formal fallacy (such as affirming the consequent), the conclusion does not follow from the premises with deductive necessity, regardless of whether the premises are factually true or false. If an argument's form is invalid, the deduction fails by definition.
            </p>
          </div>

          <div className="ql-card p-4 sm:p-5 rounded-lg border border-border/60 bg-card/50">
            <span className="fl-telemetry-tag block w-fit mb-2">Content & Context Defects</span>
            <h3 className="font-serif text-base font-bold text-foreground mb-2 flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-400 shrink-0" aria-hidden="true" />
              <span>Informal Fallacies</span>
            </h3>
            <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Errors in the <strong className="text-foreground">content, semantic meaning, or context</strong> of the argument. The premises may be irrelevant, ambiguous, unproven, or statistically biased. Evaluating informal fallacies requires examining the empirical world, linguistic nuance, sample sizes, and pragmatic relevance.
            </p>
          </div>
        </div>
      </div>

      {/* Epistemic Ground Rules */}
      <LessonStep kind="mistake" title="The Three Cardinal Rules of Fallacy Analysis">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" aria-hidden="true" />
            <div>
              <strong className="text-foreground block font-serif text-sm">
                Rule 1: Naming a Fallacy Never Refutes a Conclusion (The Fallacy Fallacy)
              </strong>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Demonstrating that an advocate used an ad hominem, straw man, or formal error proves only that <strong className="text-foreground">their specific argument fails to provide justification</strong>. It does <strong className="text-destructive">NOT</strong> establish that their conclusion is false. Committing the "fallacy fallacy" (<em>argumentum ad logicam</em>) means wrongly assuming that a poorly defended conclusion must be false. The conclusion might independently be true.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" aria-hidden="true" />
            <div>
              <strong className="text-foreground block font-serif text-sm">
                Rule 2: Fallacy Names Are Diagnostic Tools, Not Rhetorical Insults
              </strong>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Invoking Latin terms ("Ad hominem! Post hoc! Equivocation!") like battle weapons often shuts down productive dialogue. Critical thinking requires explaining <strong className="text-foreground">why the reasoning fails</strong> and helping reconstruct the strongest possible version of the opponent's claim (<Term id="steelmanning" /> and the <Term id="principle-of-charity" />), rather than labeling real people dishonest.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" aria-hidden="true" />
            <div>
              <strong className="text-foreground block font-serif text-sm">
                Rule 3: Recognize When Similar-Looking Patterns Are Entirely Reasonable
              </strong>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Context is everything in informal logic. Deferring to consensus among relevant credentialed specialists (<Term id="expertise" />) is not an appeal to authority; evaluating the emotional suffering of an injured plaintiff is not a fallacious appeal to emotion; and analyzing historical precedent to avoid known failure modes is not a blind appeal to tradition.
              </p>
            </div>
          </div>
        </div>
      </LessonStep>

      {/* Deeper Exploration Disclosure */}
      <Deeper title="Nuanced Boundary Analysis: When Patterns Are Fallacious vs Rational">
        <div className="space-y-4 text-xs sm:text-sm leading-relaxed">
          <p>
            Every informal fallacy lives adjacent to a completely rational inductive or abductive pattern of inference:
          </p>

          <ul className="list-disc pl-5 space-y-2.5">
            <li>
              <strong className="text-foreground">Expert Testimony vs Irrelevant Authority:</strong> Citing a Nobel Prize in physics to sell cryptocurrency is fallacious (<Link to="#fallacy-appeal-to-irrelevant-authority" className="text-primary hover:underline">appeal to irrelevant authority</Link>). Citing a peer-reviewed consensus of seismologists on earthquake fault slippage is rational inductive deference.
            </li>
            <li>
              <strong className="text-foreground">Relevant Emotional Evidence vs Emotional Manipulation:</strong> Evoking images of crying children to bypass environmental zoning permits is fallacious (<Link to="#fallacy-appeal-to-emotion" className="text-primary hover:underline">appeal to emotion</Link>). Documenting the physical agony of clinical trial participants suffering toxic side effects is legitimate empirical evidence of harm.
            </li>
            <li>
              <strong className="text-foreground">Slippery Slope vs Well-Evidenced Causal Cascades:</strong> Claiming bike racks lead inevitably to a crime wasteland is fallacious (<Link to="#fallacy-slippery-slope" className="text-primary hover:underline">slippery slope</Link>). Demonstrating that removing a judicial check eliminates the sole legal enforcement barrier against executive overreach is a sound legal analysis of precedent.
            </li>
            <li>
              <strong className="text-foreground">Appeal to Ignorance vs Thorough Evidence of Absence:</strong> Claiming unexplained radar signals must be alien spacecraft is fallacious (<Link to="#fallacy-appeal-to-ignorance" className="text-primary hover:underline">appeal to ignorance</Link>). Searching a sealed room thoroughly with an operational metal detector and detecting no metal is valid inductive evidence of the absence of metal.
            </li>
          </ul>
        </div>
      </Deeper>

      <ScopeNote>
        <strong>Pedagogical Standard:</strong> The Fallacy Library categorizes 54 distinct reasoning patterns using realistic fictional examples. All scientific, judicial, and statistical calculations contain mathematically verified figures to illustrate real-world error mechanisms.
      </ScopeNote>
    </div>;
}